import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Check, X } from "lucide-react";
import {
  AnswerButton,
  TRUE_FALSE_OPTION_LABELS,
  TRUE_FALSE_OPTIONS_GRID,
  TRUE_FALSE_OPTIONS_LAYOUT,
} from "@/components/storyboard/quiz/TrueFalseAnswerButton";
import QuizCheckNextAction from "@/components/storyboard/quiz/QuizCheckNextAction";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import QuizFeedback from "@/components/storyboard/quiz/QuizFeedback";
import { buildOptionsItem } from "@/components/storyboard/quiz/review/buildReviewItem";
import type { QuizTrueFalseEntryData } from "@/types/storyboard";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export type { QuizTrueFalseEntryData };

export interface QuizTrueFalseState {
  currentIndex: number;
  selectedAnswer: boolean | null;
  isSubmitted: boolean;
  isCorrect: boolean | null;
  correctCount: number;
}

export interface QuizTrueFalseProps extends HiddenVerdictProps {
  entries: QuizTrueFalseEntryData[];
  onValidate: (answer: boolean, entryIndex: number) => Promise<boolean>;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  initialPartial?: QuizTrueFalseState;
  initialIndex?: number;
  onPartialChange?: (partial: QuizTrueFalseState, index: number) => void;
}

const QuizTrueFalse = ({
  entries,
  onValidate,
  onComplete,
  initialPartial,
  initialIndex,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizTrueFalseProps) => {
  const [state, setState] = useState<QuizTrueFalseState>(() => {
    if (initialPartial) return initialPartial;
    return {
      currentIndex: initialIndex ?? 0,
      selectedAnswer: null,
      isSubmitted: false,
      isCorrect: null,
      correctCount: 0,
    };
  });

  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  const reviewRef = useRef<QuizReviewItem[]>([]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange]);

  const { isSubmitted, selectedAnswer, currentIndex, isCorrect } = state;

  if (!entries || entries.length === 0) {
    return null;
  }

  const currentEntry = entries[currentIndex];

  if (!currentEntry) {
    return null;
  }

  const handleAnswerSelect = (answer: boolean) => {
    if (!isSubmitted) {
      hasInteractedRef.current = true;
      setState((prev) => ({ ...prev, selectedAnswer: answer }));
    }
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null) return;

    if (collectsWithoutVerdict(revealFeedback)) {
      await advanceAfterCollect(
        () => onValidate(selectedAnswer, currentIndex),
        onAdvance
      );
      return;
    }

    const correct = await onValidate(selectedAnswer, currentIndex);

    const correctCount = state.correctCount + (correct ? 1 : 0);

    const correctValue = correct ? selectedAnswer : !selectedAnswer;
    reviewRef.current[currentIndex] = buildOptionsItem({
      prompt: currentEntry.statement,
      options: [
        {
          label: TRUE_FALSE_OPTION_LABELS.true,
          selected: !!selectedAnswer,
          correct: correctValue,
        },
        {
          label: TRUE_FALSE_OPTION_LABELS.false,
          selected: !selectedAnswer,
          correct: !correctValue,
        },
      ],
      isCorrect: correct,
      feedback: currentEntry.userFeedback,
    });

    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      isSubmitted: true,
      isCorrect: correct,
      correctCount,
    }));

    if (currentIndex === entries.length - 1) {
      onComplete?.(
        { correct: correctCount, total: entries.length },
        { items: reviewRef.current }
      );
      if (correct) {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      setState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        selectedAnswer: null,
        isSubmitted: false,
        isCorrect: null,
      }));
    }
  };

  return (
    <div className="flex h-full flex-col">
      <QuizEntryCounter currentIndex={currentIndex} total={entries.length}>
        <div className="prose quiz-question max-w-full font-semibold text-primary">
          <CourseMarkdown>{currentEntry.statement}</CourseMarkdown>
        </div>
      </QuizEntryCounter>

      <div className="flex flex-1 flex-col items-center justify-start gap-4 pt-8">
        <div className={TRUE_FALSE_OPTIONS_LAYOUT}>
          <TaskInstructionHint className="self-start">
            {currentEntry.instruction}
          </TaskInstructionHint>
          <div className={TRUE_FALSE_OPTIONS_GRID}>
            <AnswerButton
              value={true}
              label={TRUE_FALSE_OPTION_LABELS.true}
              icon={Check}
              isSelected={selectedAnswer === true}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onSelect={handleAnswerSelect}
            />
            <AnswerButton
              value={false}
              label={TRUE_FALSE_OPTION_LABELS.false}
              icon={X}
              isSelected={selectedAnswer === false}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onSelect={handleAnswerSelect}
            />
          </div>
        </div>
        <QuizFeedback
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
          positiveFeedback={currentEntry.userFeedback?.positive}
          negativeFeedback={currentEntry.userFeedback?.negative}
          className="max-w-2xl"
        />
      </div>

      <div className="flex shrink-0 justify-center pt-8">
        <QuizCheckNextAction
          isSubmitted={isSubmitted}
          hasNextQuestion={currentIndex < entries.length - 1}
          canSubmit={selectedAnswer !== null}
          onSubmit={handleSubmit}
          onNext={handleNext}
          submitLabel={
            collectsWithoutVerdict(revealFeedback) ? advanceLabel : undefined
          }
        />
      </div>
    </div>
  );
};

export default QuizTrueFalse;
